import R from "ramda";

import { isBlank } from "utils/storeUtils";
import { transformPoint, subtractPoints, isZeroDistance, zeroPoint } from "utils/pointUtils";
import { HEAD, matrixFor } from "utils/canvasUtils";

const MOUTH_MASK_LINE_TOP = 450;
const EYE_MASK_LINE_TOP = 250;
const JAW_DEFAULT_WIDTH = 140;

export const defaultGVisionData = (sourceImage) => {
  const { width, height } = sourceImage;
  return {
    container: {
      x: (width - HEAD.width) / 2,
      y: (height - HEAD.height) / 2,
      width: HEAD.width,
      height: HEAD.height,
    },
    faceMidPoint: { x: width / 2, y: height / 2 },
    jaw: {
      bottom: { x: HEAD.width / 2, y: HEAD.height },
      left: { x: (HEAD.width - JAW_DEFAULT_WIDTH) / 2, y: MOUTH_MASK_LINE_TOP },
      right: { x: (HEAD.width + JAW_DEFAULT_WIDTH) / 2, y: MOUTH_MASK_LINE_TOP },
      top: { x: HEAD.width / 2, y: MOUTH_MASK_LINE_TOP },
    },
  };
};

const getSafeCoordinate = (coordName, point) => R.propOr(0, coordName, point);
const getSafeXCoordinate = (point) => getSafeCoordinate("x", point);
const getSafeYCoordinate = (point) => getSafeCoordinate("y", point);

const convertKey = (key) => {
  let newKey = key.toLocaleLowerCase();
  newKey = newKey.replace(/(_\w)/g, (m) => {
    return m[1].toUpperCase();
  });

  return newKey;
};

const detectContainer = ({ eyeOutline, mouthOutline, faceMidPoint }) => {
  const ratio = HEAD.width / HEAD.height;
  const h = (HEAD.height * (mouthOutline - eyeOutline)) / (MOUTH_MASK_LINE_TOP - EYE_MASK_LINE_TOP);
  const w = h * ratio;

  return {
    x: faceMidPoint.x - w / 2,
    y: faceMidPoint.y - h / 2,
    width: w,
    height: h,
  };
};

export const gVisionToNewHead = (data, sourceImage) => {
  if (isBlank(data)) {
    return defaultGVisionData(sourceImage);
  }

  const {
    fdBoundingPoly: { vertices: faceBox },
    landmarks,
    rollAngle,
  } = data;

  const landmarksObj = landmarks.reduce(
    (acc, el) => ({ ...acc, [convertKey(el.type)]: { ...zeroPoint, ...el.position } }),
    {},
  );

  const faceSize = {
    width: getSafeXCoordinate(faceBox[1]) - getSafeXCoordinate(faceBox[0]),
    height: getSafeYCoordinate(faceBox[3]) - getSafeYCoordinate(faceBox[0]),
  };

  const rotation = rollAngle * (Math.PI / 180);

  const faceMidPoint = {
    x: getSafeXCoordinate(faceBox[0]) + faceSize.width / 2,
    y: getSafeYCoordinate(faceBox[0]) + faceSize.height / 2,
  };

  const matrix = matrixFor({ rotation, center: faceMidPoint });
  const rotate = (point) => transformPoint(matrix, point);

  const eyesY = [landmarksObj.leftEye, landmarksObj.rightEye]
    .filter((point) => !isZeroDistance(point, zeroPoint))
    .map((point) => rotate(point).y);

  const eyeOutline = eyesY.reduce((a, b) => a + b) / eyesY.length;

  const mouthOutline = rotate(landmarksObj.mouthCenter).y;

  const container = detectContainer({ eyeOutline, mouthOutline, faceMidPoint });

  const subtractContainer = (point) => subtractPoints(point, container);
  const scaleContainer = ({ x, y }) => ({
    x: (x / container.width) * HEAD.width,
    y: (y / container.height) * HEAD.height,
  });

  const convert = R.pipe(rotate, subtractContainer, scaleContainer);

  const jaw = {
    left: convert(landmarksObj.mouthLeft, "left"),
    right: convert(landmarksObj.mouthRight, "right"),
    top: convert(landmarksObj.mouthCenter, "top"),
    bottom: convert(landmarksObj.chinGnathion, "bottom"),
  };

  return {
    ...data,
    ...landmarksObj,
    faceBox,
    faceSize,
    faceMidPoint,
    eyeOutline,
    mouthOutline,
    rotation,
    container,
    jaw,
    transforms: {
      rotate,
      convert,
      scaleContainer,
    },
  };
};
